import { ApexAccounts } from 'apex-ui-services'
import { AccountDetailResponsePayload } from '@Types'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import React from 'react'
import { connect } from 'react-redux'
import { RootState } from '../../store'
import TransfersTable from './TransfersTable'
import PositionsTable from './PositionsTable'
import { Box, Stack, Skeleton, Flex, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react'
import useGetPositionJournalTransfers from '../../api/useGetPositionJournals'
import { AuthorizationState } from '@Types'
import { hasPositionJournalApiPermissions } from '../../utils/permissionsHelper'
import useGetRecentCashActivity from '../../api/useGetRecentCashActivity'
import { useGetAchRel } from '../../api/useGetAchRel'

export interface Props {
  accountNumber: string
  accountDetails?: AccountDetailResponsePayload | null
  auth?: AuthorizationState
}

export const SubmittedTransfersComponent = ({ accountNumber, accountDetails, auth }: Props) => {
  const { data: achRelationships } = useGetAchRel(accountNumber)
  const {
    data: positionJournalData,
    isLoading: isLoadingPositionJournal,
    isError: transfersPositionIsError,
    error: transferPositionError
  } = useGetPositionJournalTransfers(accountNumber)

  const twoWeeksAgo = DateTime.local().minus({ weeks: 2 })
  const today = DateTime.local()

  const {
    data: searchTransfers,
    isLoading: transfersIsLoading,
    isError: transfersIsError,
    error: transfersError
  } = useGetRecentCashActivity(accountNumber)

  const transferServerError = transfersError?.response && transfersError?.response.status >= 500
  const transferPermissionError =
    !isEmpty(transfersError?.response) && transfersError?.response && transfersError?.response.status === 403
  const noRecentActivity =
    isEmpty(transfersIsError) && !transferPermissionError && isEmpty(searchTransfers) && !transfersIsLoading

  const transferPositionServerError = transferPositionError?.response && transferPositionError?.response.status >= 500
  const transferPositionPermissionError =
    !isEmpty(transferPositionError?.response) &&
    transferPositionError?.response &&
    transferPositionError?.response.status === 403
  const noRecentJournalActivity =
    isEmpty(transfersPositionIsError) &&
    isEmpty(positionJournalData) &&
    !isLoadingPositionJournal &&
    !transferPositionPermissionError

  const hasPositionJournalApiPerm = hasPositionJournalApiPermissions(auth?.authorization?.permissions)
  return (
    <Tabs variant='enclosed'>
      <TabList position='relative'>
        <Tab>Cash</Tab>
        <Tab>Positions</Tab>
      </TabList>

      <TabPanels>
        <TabPanel p={0}>
          <Stack>
            <Flex justifyContent='center' alignItems='center'>
              {transfersIsLoading && (
                <Stack w='100%' spacing='4' data-testid='loading-transfers' marginBottom='6'>
                  <Skeleton h='20px' />
                  <Skeleton h='20px' />
                </Stack>
              )}

              {transferServerError && (
                <Box w='100%' p='4'>
                  There was a problem retrieving this information. Please try again later.
                </Box>
              )}

              {transferPermissionError && !transfersIsLoading && (
                <Box w='100%' p='4'>
                  Additional permissions are required to view this information.
                </Box>
              )}

              {noRecentActivity && !transfersIsLoading && (
                <Box w='100%' p='4'>
                  {`No activity between ${twoWeeksAgo.toLocaleString(DateTime.DATE_MED)} 
            and ${today.toLocaleString(DateTime.DATE_MED)}`}
                </Box>
              )}

              {!isEmpty(searchTransfers) && (
                <TransfersTable
                  accountNumber={accountNumber}
                  accountDetails={accountDetails}
                  achRelationships={achRelationships}
                  searchTransfers={searchTransfers}
                  permissions={auth?.authorization?.permissions}
                />
              )}
            </Flex>
          </Stack>
        </TabPanel>
        <TabPanel p={0}>
          {isLoadingPositionJournal && (
            <Stack w='100%' spacing='4' data-testid='loading transfers' marginBottom='6'>
              <Skeleton h='20px' />
              <Skeleton h='20px' />
            </Stack>
          )}
          {transferPositionServerError && (
            <Box w='100%' p='3'>
              There was a problem retrieving this information. Please try again later.
            </Box>
          )}

          {transferPositionPermissionError && !transferPositionServerError && (
            <Box w='100%' p='3'>
              Additional permissions are required to view this information.
            </Box>
          )}

          {noRecentJournalActivity && !isLoadingPositionJournal && (
            <Box w='100%' p='3'>
              {`No activity between ${twoWeeksAgo.toLocaleString(DateTime.DATE_MED)} 
            and ${today.toLocaleString(DateTime.DATE_MED)}`}
            </Box>
          )}
          {!isEmpty(positionJournalData) && positionJournalData && (
            <PositionsTable
              accountNumber={accountNumber}
              accountDetails={accountDetails}
              positionJournals={positionJournalData}
              hasPositionCancelPermission={hasPositionJournalApiPerm}
            />
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  )
}

const mapStateToProps = (state: RootState) => {
  const legitAuthState = (state as any)?.APEX_LEGIT_AUTHORIZATION
  return {
    accountDetails: ApexAccounts.accounts.selectors.accountDetails(state),
    auth: {
      authorization: legitAuthState?.results
    }
  }
}

export const SubmittedTransfers = connect(mapStateToProps)(SubmittedTransfersComponent)
